import { NEW_TEMPLATE_THRESHOLD_DAYS } from "./constants";

const SECONDS_PER_DAY = 24 * 60 * 60;

/**
 * Determines if a template should be displayed with a "NEW" badge
 * based on its creation timestamp.
 *
 * @param createdAt - Unix timestamp in seconds
 * @returns true if the template was created within the threshold period
 */
export const isTemplateNew = (createdAt: number): boolean => {
  const now = Math.floor(Date.now() / 1000);
  const thresholdSeconds = NEW_TEMPLATE_THRESHOLD_DAYS * SECONDS_PER_DAY;
  return now - createdAt < thresholdSeconds;
};

/**
 * Gets the URL for a template in the marketplace.
 *
 * @param templateId - The template ID
 * @returns The template URL path
 */
export const getTemplateMarketplaceTemplateUrl = (templateId: string): string =>
  `/templates/template/${templateId}`;

/**
 * Gets the URL for a template deployment in the marketplace.
 *
 * @param templateId - The template ID
 * @param deploymentId - The deployment ID
 * @returns The template deployment URL path
 */
export const getTemplateMarketplaceDeploymentUrl = (
  templateId: string,
  deploymentId: string
): string => `/templates/template/${templateId}/deployment/${deploymentId}`;

/**
 * Gets the relative URL for the template deployment form in the marketplace.
 * Use this for React Router navigation (to/href props).
 * Includes the "from=marketplace" query parameter to persist marketplace context.
 *
 * @param templateId - The template ID
 * @param versionId - The version ID
 * @returns The deployment form relative URL path with query parameter
 */
export const getTemplateMarketplaceDeployFormUrl = (
  templateId: string,
  versionId: string
): string => `/templates/template/${templateId}/version/${versionId}/deploy?from=marketplace`;

/**
 * Gets the absolute URL for the template deployment form in the marketplace.
 * Use this for copying to clipboard or external sharing.
 * Includes the "from=marketplace" query parameter to persist marketplace context.
 *
 * @param templateId - The template ID
 * @param versionId - The version ID
 * @returns The deployment form absolute URL with query parameter
 */
export const getTemplateMarketplaceDeployFormAbsoluteUrl = (
  templateId: string,
  versionId: string
): string =>
  `${window.location.origin}${getTemplateMarketplaceDeployFormUrl(templateId, versionId)}`;
